// Stage-Gate math — mirrors backend/routers/stages.py::stage_views.
const f = (v) => Number(v) || 0;
const EPS = 1e-6;

export const STAGE_TYPE_LABEL = { in_house: "In-House", osp: "Outside (OSP)", assembly: "Assembly" };

// Per-stage derived numbers. qty_in = order qty for stage 0, else previous stage's completed qty.
export function stageViews(order) {
  const target = f(order?.target_quantity);
  const stages = order?.stages || [];
  let prevDone = target;
  return stages.map((st, i) => {
    const done = f(st.qty_done);
    const shipped = f(st.qty_shipped);
    const isOsp = st.type === "osp";
    const available = Math.max(0, prevDone - (isOsp ? shipped : done));
    const isDone = target > 0 && done >= target - EPS;
    const view = {
      index: i,
      name: st.name,
      type: st.type || "in_house",
      isOsp,
      isFinal: i === stages.length - 1,
      qtyIn: prevDone,
      done,
      shipped,
      atVendor: isOsp ? Math.max(0, shipped - done) : 0,
      available,
      isDone,
      isActive: done > 0 || shipped > 0,
      pct: target > 0 ? Math.min(100, Math.round((done / target) * 100)) : 0,
      vendorPct: target > 0 && isOsp ? Math.min(100, Math.round((Math.max(0, shipped - done) / target) * 100)) : 0,
      waitingOn: i > 0 && !isDone && available <= EPS ? stages[i - 1].name : null,
      vendorName: st.vendor_name || "",
      gatePassCount: (st.gate_pass_ids || []).length,
    };
    prevDone = done;
    return view;
  });
}

// Whole-order quantity flow for the WIP Pipeline bar.
export function orderFlow(order) {
  const target = f(order?.target_quantity);
  const views = stageViews(order);
  if (!views.length) {
    const finished = f(order?.produced_quantity);
    return { target, finished, atVendor: 0, inHouse: 0, remaining: Math.max(0, target - finished) };
  }
  const first = views[0];
  const started = first.isOsp ? first.shipped : first.done;
  const finished = views[views.length - 1].done;
  const atVendor = views.reduce((s, v) => s + v.atVendor, 0);
  const inHouse = Math.max(0, started - finished - atVendor);
  return { target, finished, atVendor, inHouse, remaining: Math.max(0, target - started) };
}

// Human label for the order's current position in its pipeline.
export function currentStageLabel(order) {
  const views = stageViews(order);
  if (!views.length) return "—";
  if (views.every((v) => v.isDone)) return "All Stages Done";
  const active = views.find((v) => !v.isDone && (v.isActive || v.available > 0));
  if (!active) return "Not Started";
  return active.name;
}

// Stage TYPE the order is currently waiting at: "in_house" | "osp" | "assembly" | "done" | null.
export function currentStageType(order) {
  const views = stageViews(order);
  if (!views.length) return null;
  if (views.every((v) => v.isDone)) return "done";
  const active = views.find((v) => !v.isDone && (v.isActive || v.available > 0));
  const stage = active || views.find((v) => !v.isDone) || views[0];
  return stage.type || "in_house";
}
